import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, ChevronRight } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { SectionLoader } from '@/components/atoms'
import { organizationService } from '@/services/organizationService'
import { UserOrganization } from '@/types/organization'

const OrganizationsScreen = () => {
  const navigate = useNavigate()
  const [orgs, setOrgs] = useState<UserOrganization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    organizationService
      .listOrganizations()
      .then(data => { if (active) setOrgs(data) })
      .catch(err => { if (active) setError(err?.message || 'Failed to load organizations') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  if (loading) return <SectionLoader />

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6">
      <h1 className="text-2xl font-semibold text-foreground mb-1">Organizations</h1>
      <p className="text-muted-foreground mb-6">Institutional accounts you belong to.</p>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {orgs.length === 0 && !error ? (
        <Card className="p-8 text-center text-muted-foreground">
          You are not part of any organization yet.
        </Card>
      ) : (
        <div className="space-y-3">
          {orgs.map(({ organization, role }) => (
            <Card
              key={organization.id}
              className="p-4 flex items-center gap-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => navigate(`/organizations/${organization.id}`)}
            >
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <Building2 size={18} className="text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground truncate">{organization.name}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">{role}</div>
              </div>
              <ChevronRight size={18} className="text-muted-foreground" />
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default OrganizationsScreen
